import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

function Card({ title, action, children, testId }) {
  return (
    <div className="card-luxe p-5 md:p-6" data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4 gap-3">
          {title && <h3 className="font-serif text-2xl text-gold">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function BannerTab({ settings, saveSettings }) {
  const [b, setB] = useState(
    settings.banner || { enabled: false, text: "", link: "" }
  );

  useEffect(() => {
    setB({ ...(settings.banner || { enabled: false, text: "", link: "" }) });
  }, [settings]);

  const save = async () => {
    try {
      const clean = {
        enabled: !!b.enabled,
        text: (b.text || "").slice(0, 120),
        link: b.link ? b.link : null,
      };
      await saveSettings({ banner: clean });
      toast.success("Banner saved");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <Card title="Tour / Availability banner" testId="admin-banner-card">
        <p className="text-ivory-dim text-sm mb-4">
          Slim strip at the top of the site. Use for tour dates, temporary
          notices, or a hard nudge to book right now. Max 120 characters.
        </p>
        <div className="flex items-center gap-3 mb-4">
          <button
            className={b.enabled ? "btn-gold" : "btn-ghost-gold"}
            onClick={() => setB({ ...b, enabled: true })}
            data-testid="admin-banner-on"
          >
            Show
          </button>
          <button
            className={!b.enabled ? "btn-wine" : "btn-ghost-gold"}
            onClick={() => setB({ ...b, enabled: false })}
            data-testid="admin-banner-off"
          >
            Hide
          </button>
        </div>
        <label className="block">
          <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
            Banner text
          </span>
          <input
            className="input-luxe mt-2"
            maxLength={120}
            value={b.text || ""}
            onChange={(e) => setB({ ...b, text: e.target.value })}
            placeholder="✈️ Touring Melbourne Dec 15–17 · Sydney back Dec 20"
            data-testid="admin-banner-text"
          />
          <div className="text-xs text-[color:var(--muted)] mt-1">
            {(b.text || "").length} / 120
          </div>
        </label>
        <label className="block mt-4">
          <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[color:var(--muted)]">
            Link (optional — leave blank to link to WhatsApp)
          </span>
          <input
            className="input-luxe mt-2"
            value={b.link || ""}
            onChange={(e) => setB({ ...b, link: e.target.value })}
            placeholder="https://…"
            data-testid="admin-banner-link"
          />
        </label>
        <div className="flex justify-end mt-6">
          <button
            onClick={save}
            className="btn-gold"
            data-testid="admin-save-banner"
          >
            Save
          </button>
        </div>
      </Card>
    </div>
  );
}

function uid() {
  return "t-" + Math.random().toString(36).slice(2, 10);
}

export function TestimonialsTab({ settings, saveSettings }) {
  const [enabled, setEnabled] = useState(
    !!(settings.testimonials?.enabled ?? false)
  );
  const [items, setItems] = useState(settings.testimonials?.items || []);

  useEffect(() => {
    setEnabled(!!(settings.testimonials?.enabled ?? false));
    setItems(settings.testimonials?.items || []);
  }, [settings]);

  const hasSeeded = items.some((i) => i.seeded);

  const save = async () => {
    try {
      const cleaned = items
        .map((i) => ({
          id: i.id || uid(),
          quote: (i.quote || "").trim(),
          attribution: (i.attribution || "").trim(),
          featured: !!i.featured,
          ...(i.seeded ? { seeded: true } : {}),
        }))
        .filter((i) => i.quote && i.attribution);
      await saveSettings({ testimonials: { enabled, items: cleaned } });
      toast.success("Testimonials saved");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const upd = (idx, k, v) => {
    const cp = [...items];
    cp[idx] = { ...cp[idx], [k]: v };
    if (cp[idx].seeded) cp[idx].seeded = false; // once touched, no longer flagged as seed
    setItems(cp);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const cp = [...items];
    [cp[i], cp[j]] = [cp[j], cp[i]];
    setItems(cp);
  };

  const add = () =>
    setItems([
      ...items,
      { id: uid(), quote: "", attribution: "", featured: false },
    ]);

  return (
    <div className="space-y-6">
      <Card title="Testimonials" testId="admin-testimonials-card">
        <p className="text-ivory-dim text-sm mb-4">
          Client reviews shown below the OnlyFans section. All display as
          5-star. Featured items appear first.
        </p>
        {hasSeeded && (
          <div className="mb-4 text-xs p-3 rounded-lg border border-[rgba(201,169,97,0.3)] bg-[rgba(201,169,97,0.06)] text-gold-2">
            Some entries below are <b>sample content</b> — replace with your
            own before showing to clients.
          </div>
        )}
        <div className="flex items-center gap-3 mb-6">
          <button
            className={enabled ? "btn-gold" : "btn-ghost-gold"}
            onClick={() => setEnabled(true)}
            data-testid="admin-testimonials-on"
          >
            Show section
          </button>
          <button
            className={!enabled ? "btn-wine" : "btn-ghost-gold"}
            onClick={() => setEnabled(false)}
            data-testid="admin-testimonials-off"
          >
            Hide
          </button>
        </div>

        <div className="space-y-4">
          {items.map((it, i) => (
            <div
              key={it.id || i}
              className="p-4 rounded-xl border border-[rgba(201,169,97,0.18)] bg-[rgba(255,255,255,0.02)]"
              data-testid={`admin-testimonial-${i}`}
            >
              <div className="flex justify-between items-center mb-3 gap-2">
                <span className="text-xs uppercase tracking-widest text-[color:var(--muted)]">
                  #{i + 1}
                  {it.seeded && (
                    <span className="ml-2 pill !py-0 !px-2 !text-[0.55rem]">
                      sample
                    </span>
                  )}
                </span>
                <div className="flex gap-1">
                  <label className="flex items-center gap-2 text-xs text-ivory-dim mr-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[color:var(--gold)]"
                      checked={!!it.featured}
                      onChange={(e) => upd(i, "featured", e.target.checked)}
                    />
                    Featured
                  </label>
                  <button
                    className="btn-ghost-gold !py-1 !px-2 !text-xs"
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="btn-ghost-gold !py-1 !px-2 !text-xs"
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    className="btn-ghost-gold !py-1 !px-3 !text-xs"
                    onClick={() => setItems(items.filter((_, j) => j !== i))}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <textarea
                className="input-luxe resize-y mb-2"
                rows={3}
                value={it.quote || ""}
                onChange={(e) => upd(i, "quote", e.target.value)}
                placeholder="Quote"
              />
              <input
                className="input-luxe !py-2"
                value={it.attribution || ""}
                onChange={(e) => upd(i, "attribution", e.target.value)}
                placeholder="Attribution (e.g. J, Sydney • 1hr booking)"
              />
            </div>
          ))}
        </div>

        <div className="flex justify-between mt-6">
          <button
            onClick={add}
            className="btn-ghost-gold"
            data-testid="admin-add-testimonial"
          >
            + Add testimonial
          </button>
          <button
            onClick={save}
            className="btn-gold"
            data-testid="admin-save-testimonials"
          >
            Save
          </button>
        </div>
      </Card>
    </div>
  );
}
